import React, { useState } from 'react';
import { X } from 'phosphor-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import './SignInSheet.css';

/**
 * Reusable sign-in surface, presented as a modal sheet. Used by the
 * save-prompt moments in MatchEditor (after FINAL) and LandingPage
 * (banner with multiple anon matches).
 *
 * When the current user is anonymous, AuthContext's signInWith*
 * methods automatically link / migrate, so this is just a UI shell
 * over the standard auth flows.
 *
 * Props:
 *   - open: boolean — whether to render
 *   - onClose: () => void — called on dismiss / successful sign-in
 *   - title: string — headline above the buttons (e.g. "Save your match")
 *   - subtitle: string — supporting copy
 */
function SignInSheet({ open, onClose, title, subtitle }) {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const toast = useToast();
  const [mode, setMode] = useState('default'); // 'default' | 'email'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isSignUp, setIsSignUp] = useState(true); // anon users almost always need to sign up
  const [isLoading, setIsLoading] = useState(false);

  if (!open) return null;

  const handleGoogle = async () => {
    setIsLoading(true);
    const result = await signInWithGoogle();
    setIsLoading(false);
    if (result.success) {
      toast.success('Saved to your account');
      onClose();
    } else if (result.error) {
      toast.error(result.error);
    }
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setIsLoading(true);
    const result = isSignUp
      ? await signUpWithEmail(email, password, displayName || null)
      : await signInWithEmail(email, password);
    setIsLoading(false);
    if (result.success) {
      toast.success(isSignUp ? 'Account created — match saved' : 'Saved to your account');
      onClose();
    } else if (result.error) {
      toast.error(result.error);
    }
  };

  return (
    <div
      className="signin-modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="signin-card" onClick={(e) => e.stopPropagation()}>
        <button
          className="close-btn"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <X size={16} weight="bold" />
        </button>

        <div className="signin-header">
          <h2 className="signin-title">{title}</h2>
          {subtitle && <p className="signin-subtitle">{subtitle}</p>}
        </div>

        {mode === 'default' && (
          <div className="signin-actions">
            <button
              className="auth-btn google-btn signin-google-btn"
              onClick={handleGoogle}
              disabled={isLoading}
              type="button"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt=""
              />
              Continue with Google
            </button>

            <button
              className="auth-link signin-link"
              onClick={() => setMode('email')}
              disabled={isLoading}
              type="button"
            >
              Use email instead
            </button>

            <button
              className="auth-link signin-dismiss"
              onClick={onClose}
              disabled={isLoading}
              type="button"
            >
              Not now
            </button>
          </div>
        )}

        {mode === 'email' && (
          <form className="signin-email" onSubmit={handleEmail}>
            {isSignUp && (
              <input
                type="text"
                placeholder="Display name (optional)"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={isLoading}
              />
            )}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
            />
            <button
              className="auth-btn email-btn"
              type="submit"
              disabled={isLoading}
            >
              {isLoading
                ? 'Please wait…'
                : isSignUp
                  ? 'Create account & save match'
                  : 'Sign in & save match'}
            </button>
            <button
              className="auth-link"
              type="button"
              onClick={() => setIsSignUp((v) => !v)}
              disabled={isLoading}
            >
              {isSignUp
                ? 'Already have an account? Sign in'
                : 'New here? Create an account'}
            </button>
            <button
              className="auth-link signin-back"
              type="button"
              onClick={() => setMode('default')}
              disabled={isLoading}
            >
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default SignInSheet;
